import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  addPublicPageReviewComment,
  approvePublicPageReview,
  fetchPublicPageReview,
  requestChangesPublicPageReview,
  resolvePublicPageReviewComment,
  type PageReviewDetailRecord,
} from "@/hooks/use-page-reviews";
import { ReviewWorkspace } from "@/routes/pages/review";

export default function PublicReviewPage() {
  const { shareToken } = useParams();
  const [detail, setDetail] = useState<PageReviewDetailRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!shareToken) return;
    try {
      const nextDetail = await fetchPublicPageReview(shareToken);
      setDetail(nextDetail);
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Failed to load review link");
    }
  };

  useEffect(() => {
    void load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  if (!shareToken) {
    return <div className="mx-auto max-w-3xl px-6 py-16 text-sm text-destructive">Review token is missing.</div>;
  }

  if (error) {
    return <div className="mx-auto max-w-3xl px-6 py-16 text-sm text-destructive">{error}</div>;
  }

  if (!detail) {
    return <div className="mx-auto max-w-3xl px-6 py-16 text-sm text-muted-foreground">Loading review surface…</div>;
  }

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.08),_transparent_36%),linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <ReviewWorkspace
          detail={detail}
          mode="public"
          actorName={detail.reviewerName ?? detail.reviewerEmail ?? "Guest reviewer"}
          onApprove={async () => {
            await approvePublicPageReview(shareToken);
            await load();
          }}
          onRequestChanges={async () => {
            await requestChangesPublicPageReview(shareToken);
            await load();
          }}
          onAddComment={async (componentId, body, position) => {
            await addPublicPageReviewComment(shareToken, {
              versionId: detail.version.id,
              componentId,
              body,
              position,
            });
            await load();
          }}
          onResolveComment={async (commentId) => {
            await resolvePublicPageReviewComment(shareToken, commentId);
            await load();
          }}
        />
      </div>
    </main>
  );
}
